"use client";
import { getAuthMenuByWorkspace } from "@/data/memo-menu-auth-data";
import { useTranslations } from "next-intl";
import Link from "next/link";
import React, { useContext } from "react";
import { useRouter } from "next/navigation";
import { AppContext } from "@/contextApi/AppProvider";
import { AppContextType, WorkspaceType } from "@/interFace/general";
import { getLink, Routes } from "@/constants";
import { Translations } from "@/translations";

const MAX_MENU_PROFILE_LABEL_LENGTH = 10;

const truncateMenuProfileLabel = (label: string) => {
  if (label.length <= MAX_MENU_PROFILE_LABEL_LENGTH) {
    return label;
  }

  return `${label.slice(0, MAX_MENU_PROFILE_LABEL_LENGTH - 1)}...`;
};

const MemoAuthMenu = () => {
  const t = useTranslations("Menu");
  const tInbox = useTranslations("Inbox");
  const router = useRouter();
  const { toggleSideMenu, sideMenuOpen, locale, currentUser, userInfo, setUserInfo, unreadCount, invitationCount, bookingNotificationCount, contactCount, challengeCount } = useContext(
    AppContext
  ) as AppContextType;
  const menuData = getAuthMenuByWorkspace(userInfo.activeWorkspace, userInfo.activeRole, userInfo.ageGroup);
  const inboxBadgeCount = unreadCount + invitationCount + contactCount;
  const profileLabel =
    currentUser?.displayName?.trim() ||
    currentUser?.providerData?.find((provider: { displayName?: string | null }) => provider?.displayName?.trim())?.displayName?.trim() ||
    currentUser?.email?.split("@")[0] ||
    t(Translations.Menu.Profile);
  const menuProfileLabel = truncateMenuProfileLabel(profileLabel);

  const getMenuLabel = (title: string, translationNamespace?: "Menu" | "Inbox") => {
    if (translationNamespace === "Inbox") {
      return tInbox(title);
    }

    return t(title);
  };

  const getWorkspaceSwitchTitle = (workspace: WorkspaceType) => {
    if (workspace === "claimant") {
      return t(Translations.Menu.SwitchToClaimantWorkspace);
    }

    if (workspace === "tutor") {
      return t(Translations.Menu.SwitchToTutorWorkspace);
    }

    if (workspace === "parent") {
      return t(Translations.Menu.SwitchToParentWorkspace);
    }

    return t(Translations.Menu.SwitchToStudentWorkspace);
  };

  const getWorkspaceSwitchDescription = (workspace: WorkspaceType) => {
    if (workspace === "claimant") {
      return t(Translations.Menu.OpenClaimantWorkspace);
    }

    if (workspace === "tutor") {
      return t(Translations.Menu.OpenTutorWorkspace);
    }

    if (workspace === "parent") {
      return t(Translations.Menu.OpenParentWorkspace);
    }

    return t(Translations.Menu.OpenStudentWorkspace);
  };

  const getWorkspaceLandingUrl = (workspace: WorkspaceType) => {
    if (workspace === "claimant") {
      return getLink(locale, Routes.CLAIMANT_ROOT);
    }

    if (workspace === "tutor") {
      return getLink(locale, Routes.TUTORS_ROOT);
    }

    if (workspace === "parent") {
      return getLink(locale, Routes.PARENTS_ROOT);
    }

    return getLink(locale, Routes.DASHBOARD_COURSES);
  };

  const getWorkspaceRole = (workspace: WorkspaceType) => {
    if (workspace === "claimant") {
      return "claimant";
    }

    if (workspace === "tutor") {
      return "tutor";
    }

    if (workspace === "parent") {
      return "parent";
    }

    return "student";
  };

  const switchTargetWorkspaces = userInfo.workspaces.filter((workspace) => {
    return workspace !== userInfo.activeWorkspace;
  });

  const handleWorkspaceSwitch = (workspace: WorkspaceType) => {
    setUserInfo((previous) => ({
      ...previous,
      activeWorkspace: workspace,
      activeRole: getWorkspaceRole(workspace),
    }));

    if (sideMenuOpen) {
      toggleSideMenu?.();
    }

    router.push(getWorkspaceLandingUrl(workspace));
  };

  return (
    <>
      {menuData?.length &&
        menuData?.map((item) => (
          <li
            key={item.id}
            className={item.hasDropdown ? "menu-item-has-children mr-15" : ""}
          >
            <Link
              onClick={() => {
                if (sideMenuOpen) {
                  toggleSideMenu?.();
                }
              }}
              href={getLink(locale, item.link)}
              className="text-decoration-none"
              title={item.isProfile ? profileLabel : item.iconClass ? getMenuLabel(item.title, item.translationNamespace) : undefined}
            >
              {" "}
              {item.iconClass ? (
                <>
                  <i className={item.iconClass} aria-hidden="true"></i>
                  <span className="visually-hidden">{getMenuLabel(item.title, item.translationNamespace)}</span>
                </>
              ) : item.title && item.title !== "PROFILE_NAME"
                ? getMenuLabel(item.title, item.translationNamespace)
                : ""}

              {item.isInbox && inboxBadgeCount > 0 && (
                <span className="badge bg-danger rounded-pill ms-1">{inboxBadgeCount}</span>
              )}

              {item.isBookings && bookingNotificationCount > 0 && (
                <span className="badge bg-danger rounded-pill ms-1" aria-label="Pending booking notifications">
                  <i className="bi bi-bell-fill"></i>
                </span>
              )}

              {item.isProfile
                ? (
                    <>{menuProfileLabel}</>)
                : ""}
                
              {item.hasPlayMenu
                ? (
                    <>{challengeCount > 0 && (
                          <span className="badge bg-danger rounded-pill ms-1">
                            <i className="bi bi-lightning-fill"></i>
                          </span>
                        )}
                    </>)
                : ""}
            </Link>

            {item.hasDropdown === true && (
              <ul className="submenu">
                {item.isProfile && switchTargetWorkspaces.map((workspace) => (
                  <li key={workspace}>
                    <button
                      type="button"
                      onClick={() => handleWorkspaceSwitch(workspace)}
                      className="text-decoration-none d-flex align-items-center justify-content-between gap-3"
                      style={{
                        background: "none",
                        border: 0,
                        padding: "10px 0",
                        color: "inherit",
                        textAlign: "left",
                        width: "100%",
                      }}
                    >
                      <span className="d-flex flex-column">
                        <span style={{ fontWeight: 600 }}>
                          {getWorkspaceSwitchTitle(workspace)}
                        </span>
                        <span style={{ fontSize: "12px", opacity: 0.75 }}>
                          {getWorkspaceSwitchDescription(workspace)}
                        </span>
                      </span>
                      <span
                        aria-hidden="true"
                        style={{ fontSize: "18px", lineHeight: 1, opacity: 0.7 }}
                      >
                        ›
                      </span>
                    </button>
                  </li>
                ))}
                {item?.submenus?.length &&
                  item?.submenus.map((data, index) => (
                    <li key={index}>
                      <Link
                        onClick={() => {
                          if (sideMenuOpen) {
                            toggleSideMenu?.();
                          }
                        }}
                        className="text-decoration-none"
                        href={getLink(locale, data.link)}
                      >
                        {" "}
                        {getMenuLabel(data.title, data.translationNamespace)}{" "}
                        {data.link === Routes.INBOX && unreadCount > 0 && (
                          <span className="badge bg-danger rounded-pill ms-1">{unreadCount}</span>
                        )}
                        {data.link === Routes.FRIENDS && invitationCount > 0 && (
                          <span className="badge bg-warning text-dark rounded-pill ms-1">{invitationCount}</span>
                        )}
                        {data.link === Routes.CONTACTS && contactCount > 0 && (
                          <span className="badge bg-success rounded-pill ms-1">{contactCount}</span>
                        )}
                      </Link>
                    </li>
                  ))}
              </ul>
            )}
          </li>
        ))}
    </>
  );
};

export default MemoAuthMenu;
